import { useEffect, useMemo, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Search, FlaskConical, X,
  Save, IndianRupee, ListChecks, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CATS = [
  "All", "Hematology", "Biochemistry", "Serology", "Urine",
  "Hormones", "Microbiology", "Pathology", "Other",
];
const CAT_STYLE = {
  Hematology:   "bg-rose-50 text-rose-700 border-rose-200",
  Biochemistry: "bg-amber-50 text-amber-700 border-amber-200",
  Serology:     "bg-indigo-50 text-indigo-700 border-indigo-200",
  Urine:        "bg-yellow-50 text-yellow-700 border-yellow-200",
  Hormones:     "bg-violet-50 text-violet-700 border-violet-200",
  Microbiology: "bg-teal-50 text-teal-700 border-teal-200",
  Pathology:    "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  Other:        "bg-slate-50 text-slate-700 border-slate-200",
};

const EMPTY_PARAM = {
  name: "", unit: "", ref_range: "", ref_range_male: "", ref_range_female: "",
  method: "", decimals: 1, type: "number", group: "",
};
const EMPTY_TEMPLATE = {
  code: "", name: "", category: "Hematology", sample_type: "",
  tat_hours: 24, price: 0, method: "", parameters: [], active: true,
};

/* ──────────────────────────── Main Page ──────────────────────────── */
export default function LabMaster() {
  const { user } = useAuth();
  const canWrite = ["admin", "super_admin", "lab"].includes(user?.role || "");

  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [priceEditId, setPriceEditId] = useState(null);
  const [priceDraft, setPriceDraft] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/lab-templates", {
        params: { q: q.trim(), category: cat === "All" ? "" : cat },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load lab templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, cat]);

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const c = r.category || "Other";
      if (!g[c]) g[c] = [];
      g[c].push(r);
    });
    return g;
  }, [rows]);

  const counts = useMemo(() => {
    const c = { All: rows.length };
    CATS.slice(1).forEach(k => { c[k] = rows.filter(r => (r.category || "Other") === k).length; });
    return c;
  }, [rows]);

  const openNew = () => { setEditing({ ...EMPTY_TEMPLATE, parameters: [] }); setDlgOpen(true); };
  const openEdit = (r) => {
    setEditing({ ...EMPTY_TEMPLATE, ...r, parameters: (r.parameters || []).map(p => ({ ...EMPTY_PARAM, ...p })) });
    setDlgOpen(true);
  };

  const savePrice = async (id) => {
    const price = parseFloat(priceDraft);
    if (isNaN(price) || price < 0) return toast.error("Invalid price");
    try {
      await api.put(`/lab-templates/${id}/price`, { price });
      toast.success(`Price updated to ₹${price}`);
      setPriceEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update price");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete lab template "${r.name}"?\nHistorical reports remain intact.`)) return;
    try {
      await api.delete(`/lab-templates/${r.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="lab-master-page">
      {/* ─── Header ─── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-emerald-700" />
            Lab Test Master
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Pre-defined pathology / biochemistry / serology templates with reference ranges and editable per-hospital pricing. Patients receive a QR-code-shareable report once results are verified.
          </p>
        </div>
        {canWrite && (
          <Button data-testid="lab-new-btn" onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800">
            <Plus className="w-4 h-4 mr-1" /> New Test Template
          </Button>
        )}
      </div>

      {/* ─── Filter bar ─── */}
      <div className="bg-white border rounded-sm p-3 flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input
            data-testid="lab-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search test or code (e.g. CBC, HbA1c, LFT…)" className="pl-7 h-9"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {CATS.map((c) => (
            <button key={c} data-testid={`lab-cat-${c}`}
              onClick={() => setCat(c)}
              className={`h-9 px-3 text-xs border rounded-sm whitespace-nowrap ${
                cat === c
                  ? "border-emerald-700 bg-emerald-50 text-emerald-800 font-semibold"
                  : "border-slate-300 text-slate-600 hover:border-emerald-400"
              }`}
            >
              {c} <span className="text-[10px] opacity-70">({counts[c] ?? 0})</span>
            </button>
          ))}
        </div>
      </div>

      {/* ─── Rows ─── */}
      <div className="bg-white border rounded-sm overflow-hidden">
        {loading && <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-slate-500">
            <FlaskConical className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            No lab templates match this filter.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="label-tiny p-2 w-24">Code</th>
                <th className="label-tiny">Name</th>
                <th className="label-tiny w-32">Category</th>
                <th className="label-tiny">Sample / TAT</th>
                <th className="label-tiny w-16 text-center">Params</th>
                <th className="label-tiny text-right w-36">Price (₹)</th>
                <th className="label-tiny w-20"></th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(grouped).sort().map((category) => (
                <>
                  <tr key={`h-${category}`} className="bg-slate-100/80">
                    <td colSpan={7} className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                      {category} · {grouped[category].length} tests
                    </td>
                  </tr>
                  {grouped[category].map((r) => (
                    <tr key={r.id} className="border-b hover:bg-emerald-50/40" data-testid={`lab-row-${r.id}`}>
                      <td className="p-2 font-mono text-xs text-slate-600">{r.code || "—"}</td>
                      <td className="font-medium text-slate-800">{r.name}</td>
                      <td>
                        <Badge className={`rounded-sm text-[10px] border ${CAT_STYLE[r.category] || CAT_STYLE.Other}`}>
                          {r.category}
                        </Badge>
                      </td>
                      <td className="text-xs text-slate-600">
                        {r.sample_type ? <span>{r.sample_type}</span> : <span className="text-slate-400">—</span>}
                        <span className="text-slate-400"> · </span>
                        <span>TAT {r.tat_hours}h</span>
                      </td>
                      <td className="text-center">
                        <Badge variant="outline" className="text-[10px] font-mono">{(r.parameters || []).length}</Badge>
                      </td>
                      <td className="text-right">
                        {priceEditId === r.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              data-testid={`lab-price-input-${r.id}`}
                              value={priceDraft} onChange={(e) => setPriceDraft(e.target.value)}
                              type="number" className="h-7 w-20 text-right font-mono"
                              autoFocus onKeyDown={(e) => { if (e.key === "Enter") savePrice(r.id); if (e.key === "Escape") setPriceEditId(null); }}
                            />
                            <button data-testid={`lab-price-save-${r.id}`} onClick={() => savePrice(r.id)}
                              className="p-1 bg-emerald-700 text-white rounded-sm hover:bg-emerald-800"
                              title="Save"><Save className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setPriceEditId(null)}
                              className="p-1 text-slate-500 hover:bg-slate-100 rounded-sm"
                              title="Cancel"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        ) : (
                          <button
                            data-testid={`lab-price-edit-${r.id}`}
                            onClick={() => { if (canWrite) { setPriceDraft(r.price); setPriceEditId(r.id); } }}
                            disabled={!canWrite}
                            className={`font-mono text-sm ${canWrite ? "hover:bg-emerald-50 px-2 py-0.5 rounded-sm" : ""}`}
                            title={canWrite ? "Click to edit price" : ""}
                          >
                            {fmtINR(r.price)}
                            {canWrite && <IndianRupee className="w-3 h-3 inline-block ml-1 text-emerald-700/60" />}
                          </button>
                        )}
                      </td>
                      <td>
                        {canWrite && (
                          <div className="flex gap-1 justify-end pr-2">
                            <button data-testid={`lab-edit-${r.id}`} onClick={() => openEdit(r)}
                              className="p-1 text-emerald-700 hover:bg-emerald-50 rounded-sm" title="Edit template">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button data-testid={`lab-del-${r.id}`} onClick={() => remove(r)}
                              className="p-1 text-rose-600 hover:bg-rose-50 rounded-sm" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Edit / Create Dialog ─── */}
      {dlgOpen && editing && (
        <TemplateEditor
          value={editing}
          onClose={() => { setDlgOpen(false); setEditing(null); }}
          onSaved={() => { setDlgOpen(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────── Template Edit / Create Dialog ─────────────────── */
function TemplateEditor({ value, onClose, onSaved }) {
  const [form, setForm] = useState(value);
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;

  const setParam = (idx, patch) => {
    const next = [...form.parameters];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, parameters: next });
  };
  const addParam = () => setForm({ ...form, parameters: [...form.parameters, { ...EMPTY_PARAM }] });
  const removeParam = (idx) => setForm({ ...form, parameters: form.parameters.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Test name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.toUpperCase().trim(),
        code: (form.code || "").toUpperCase().trim(),
        price: parseFloat(form.price) || 0,
        tat_hours: parseInt(form.tat_hours) || 24,
      };
      if (isNew) await api.post("/lab-templates", payload);
      else       await api.put(`/lab-templates/${form.id}`, payload);
      toast.success(isNew ? "Template created" : "Template updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-emerald-700" />
            {isNew ? "New Lab Test Template" : "Edit Lab Test Template"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* basic info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Code</Label>
              <Input data-testid="lab-form-code"
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. CBC" />
            </div>
            <div className="col-span-2">
              <Label>Test Name *</Label>
              <Input data-testid="lab-form-name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. COMPLETE BLOOD COUNT (CBC)" />
            </div>
            <div>
              <Label>Category *</Label>
              <select data-testid="lab-form-cat"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-9 border border-slate-300 w-full px-2 text-sm rounded-sm">
                {CATS.filter(c => c !== "All").map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Price (₹) *</Label>
              <Input type="number" data-testid="lab-form-price"
                value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label>TAT (hrs)</Label>
              <Input type="number" data-testid="lab-form-tat"
                value={form.tat_hours} onChange={(e) => setForm({ ...form, tat_hours: e.target.value })} />
            </div>
            <div>
              <Label>Sample Type</Label>
              <Input data-testid="lab-form-sample"
                value={form.sample_type} onChange={(e) => setForm({ ...form, sample_type: e.target.value })}
                placeholder="e.g. EDTA blood" />
            </div>
            <div>
              <Label>Method</Label>
              <Input data-testid="lab-form-method"
                value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                placeholder="e.g. HPLC / CLIA" />
            </div>
          </div>

          {/* Parameters editor */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-emerald-700" />
                Parameters & Reference Ranges
                <span className="text-xs text-slate-500 font-normal">
                  — one row per test parameter (e.g. "Haemoglobin", "WBC Count")
                </span>
              </h3>
              <Button variant="outline" size="sm" data-testid="lab-add-param" onClick={addParam}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Parameter
              </Button>
            </div>

            {form.parameters.length === 0 && (
              <div className="border rounded-sm p-5 text-center text-xs text-slate-500 bg-slate-50">
                <AlertCircle className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                No parameters yet. Add rows for every value the lab tech will enter — the public QR report will show
                each parameter with its normal range and automatic High/Low flag.
              </div>
            )}

            {form.parameters.length > 0 && (
              <div className="border rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="label-tiny p-1 w-6"></th>
                      <th className="label-tiny p-1 min-w-[160px]">Parameter *</th>
                      <th className="label-tiny p-1 w-20">Unit</th>
                      <th className="label-tiny p-1 min-w-[140px]">Ref. Range</th>
                      <th className="label-tiny p-1 min-w-[120px]">Male (opt.)</th>
                      <th className="label-tiny p-1 min-w-[120px]">Female (opt.)</th>
                      <th className="label-tiny p-1 w-20">Type</th>
                      <th className="label-tiny p-1 w-24">Group</th>
                      <th className="label-tiny p-1 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.parameters.map((p, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-1 text-center text-slate-400 font-mono text-[10px]">{idx + 1}</td>
                        <td className="p-1"><Input value={p.name} onChange={(e) => setParam(idx, { name: e.target.value })} className="h-7 text-xs" data-testid={`lab-param-name-${idx}`} /></td>
                        <td className="p-1"><Input value={p.unit} onChange={(e) => setParam(idx, { unit: e.target.value })} className="h-7 text-xs" /></td>
                        <td className="p-1"><Input value={p.ref_range} onChange={(e) => setParam(idx, { ref_range: e.target.value })} className="h-7 text-xs" placeholder="e.g. 70 - 100" /></td>
                        <td className="p-1"><Input value={p.ref_range_male} onChange={(e) => setParam(idx, { ref_range_male: e.target.value })} className="h-7 text-xs" placeholder="e.g. 13 - 17" /></td>
                        <td className="p-1"><Input value={p.ref_range_female} onChange={(e) => setParam(idx, { ref_range_female: e.target.value })} className="h-7 text-xs" placeholder="e.g. 12 - 15" /></td>
                        <td className="p-1">
                          <select value={p.type || "number"} onChange={(e) => setParam(idx, { type: e.target.value })} className="h-7 text-xs border rounded-sm w-full px-1">
                            <option value="number">Number</option>
                            <option value="text">Text</option>
                          </select>
                        </td>
                        <td className="p-1"><Input value={p.group} onChange={(e) => setParam(idx, { group: e.target.value })} className="h-7 text-xs" placeholder="e.g. Differential" /></td>
                        <td className="p-1">
                          <button onClick={() => removeParam(idx)} className="p-1 text-rose-600 hover:bg-rose-50 rounded-sm" title="Remove parameter">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t pt-3 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="lab-form-save" onClick={save} disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800">
            {saving ? "Saving…" : (isNew ? "Create Template" : "Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
